import { ApolloError, MutationResult, useMutation } from '@apollo/client';

import { DocumentNode } from 'graphql';

interface ErrorResult {
  hasError: true;
  data: never;
  error: ApolloError;
}

interface SuccessResult<TData> {
  hasError: false;
  data: TData;
  error: never;
}

/**
 * By explicitly defining the results with the `hasError` flag, typescript will
 * know that data is not null or undefined when `hasError` is false.
 */

export type SafeMutationResult<TData> = ErrorResult | SuccessResult<TData>;

/**
 * Wrapper for `useMutation` that wraps the returned mutation in a
 * function that captures any thrown exception or any errors returned
 * from the mutation and returns either `{ hasError: true, error: ApolloError }` or
 * `{ hasError: false, data: TData }` and does _not_ throw an exception.
 *
 * This function frees you from having to work out how to deal with a
 * thrown exception vs an error that is returned from the mutation.
 *
 * This function will also return an error if the result of the mutation
 * is null or undefined.
 *
 * @param mutation gql to create mutation from
 * @param options the options to pass to useMutation
 */

export function useSafeMutation<TData, TVariables>(
  mutation: DocumentNode,
  options = {},
): [( variables: TVariables ) => Promise<SafeMutationResult<TData>>, MutationResult<TData>] {
  const [mutateFn, ...rest] = useMutation<TData, TVariables>( mutation, options );

  const safeMutate = async ( variables: TVariables ) => {
    try {
      const result = await mutateFn( { variables } );

      if ( result.errors?.length ) {
        return {
          hasError: true,
          error: new ApolloError( { graphQLErrors: result.errors } ),
        } as ErrorResult;
      }

      if ( result.data === null || typeof result.data === 'undefined' ) {
        return {
          hasError: true,
          error: new ApolloError( { errorMessage: 'No result returned from server' } ),
        } as ErrorResult;
      }

      return {
        hasError: false,
        data: result.data as NonNullable<TData>,
      } as SuccessResult<TData>;
    } catch ( error ) {
      return {
        hasError: true,
        error: error instanceof ApolloError ? error : new ApolloError( { errorMessage: String( error ) } ),
      } as ErrorResult;
    }
  };

  return [safeMutate, ...rest] as const;
}
